import { Component, Input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { from, map, of, switchMap } from 'rxjs';
import { Gastro } from 'src/app/maps/domain/masterdata/gastro.model';
import { OperatingHours } from 'src/app/maps/domain/masterdata/operating-hours.model';
import { POI } from 'src/app/maps/domain/masterdata/poi.model';
import { IconService } from 'src/app/maps/map/icon-service';
import { MapStateService } from 'src/app/maps/map/map-state.service';
import { MapStyleService } from 'src/app/maps/map/style/map-style.service';

@Component({
  selector: 'sis-asset-sidepane-content',
  templateUrl: './asset-sidepane-content.component.html',
  styleUrls: ['./asset-sidepane-content.component.scss'],
  standalone: false,
})
export class AssetSidepaneContentComponent {
  @Input() showQrCode = false;

  readonly element$ = this.mapStateService.selectedElement$.pipe(map(({ element }) => element));
  readonly className$ = from(this.mapStyleService.getStyleName()).pipe(map((name) => `sis-menu-${name}`));

  readonly assetTypeIcon$ = this.element$.pipe(
    switchMap((element) => {
      if (element?.iconImageName) {
        return from(this.iconService.getAssetTypeIconHtml(element)).pipe(
          map((iconString) => this.sanitizer.bypassSecurityTrustHtml(iconString)),
        );
      }
      return of(null);
    }),
  );

  readonly operatingHours$ = this.element$.pipe(
    map((element) => (element ? this.createOperatingHours((element as POI | Gastro).operatingHours) : [])),
  );

  constructor(
    private mapStyleService: MapStyleService,
    private mapStateService: MapStateService,
    private iconService: IconService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
  ) {}

  private createOperatingHours(operatingHours: OperatingHours[]) {
    const today = new Date();

    const todayWeekday = today.getDay() || 7;
    const upcomingWeekdays = Array.from({ length: 7 }, (_, i) => ((todayWeekday - 1 + i) % 7) + 1);

    return operatingHours
      .filter((o) => upcomingWeekdays.includes(o.weekday))
      .sort((a, b) => upcomingWeekdays.indexOf(a.weekday) - upcomingWeekdays.indexOf(b.weekday))
      .map((o) => {
        const daysAhead = (o.weekday - todayWeekday + 7) % 7;

        const date = new Date(today);
        date.setDate(today.getDate() + daysAhead);

        const weekdayLabel = this.translateService.instant(`general.term.weekday.${o.weekday}`);
        const dateLabel = `${date.getDate()}.${date.getMonth() + 1}.`;

        let time = '';
        switch (o.status) {
          case 0:
            time = this.translateService.instant('operatinghours.term.closed');
            break;
          case 1:
            time = `${o.from1}–${o.until1}` + (o.from2 && o.until2 ? `, ${o.from2}–${o.until2}` : '');
            break;
          case 2:
            time = `${o.from1}–${o.until2}`;
            break;
        }

        return { weekday: weekdayLabel, date: dateLabel, time, isToday: daysAhead === 0 };
      });
  }
}
